import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Stack,
  Typography,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import dayjs from "dayjs";
import api from "../api/axios";

export default function BookingForm({
  selectedSlot,
  onReset,
  onBookingSuccess,
  SLOT_MINUTES,
}) {
  const [form, setForm] = useState({
    startTime: null,
    endTime: null,
    convenor: "",
    purpose: "",
    attendees: "",
  });

  const [isViewMode, setIsViewMode] = useState(false);

  useEffect(() => {
    if (!selectedSlot) {
      resetForm();
      return;
    }

    if (selectedSlot.isExisting && selectedSlot.booking) {
      // VIEW MODE (existing booking)
      setForm({
        startTime: dayjs(selectedSlot.booking.start),
        endTime: dayjs(selectedSlot.booking.end),
        convenor: selectedSlot.booking.convenor || "",
        purpose: selectedSlot.booking.purpose || "",
        attendees: selectedSlot.booking.attendees || "",
      });
      setIsViewMode(true);
    } else {
      // NEW BOOKING MODE
      const start = selectedSlot.start;
      setForm({
        startTime: start,
        endTime: start.add(SLOT_MINUTES, "minute"),
        convenor: "",
        purpose: "",
        attendees: "",
      });
      setIsViewMode(false);
    }
  }, [selectedSlot]);

  const incrementEndTime = () => {
    setForm((p) => ({
      ...p,
      endTime: p.endTime.add(SLOT_MINUTES, "minute"),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.startTime || !form.convenor || !form.purpose) {
      alert("Please fill the booking form.");
    } else {
      const { data: response } = await api.post("/conference-room/book", {
        start: form.startTime.format(),
        end: form.endTime.format(),
        convenor: form.convenor,
        purpose: form.purpose,
        attendees: form.attendees,
      });
      console.log(response);
      if (response.success) {
        alert(response.message);
        onReset();
        onBookingSuccess();
      } else {
        alert(response.error);
      }
    }
  };

  const resetForm = () => {
    setForm({
      startTime: null,
      endTime: null,
      convenor: "",
      purpose: "",
      attendees: "",
    });
    setIsViewMode(false);
  };

  return (
    <Box p={3}>
      <Typography variant="h6" mb={2}>
        Book Conference Room
      </Typography>

      {isViewMode && (
        <Typography variant="caption" color="warning.main" sx={{ mb: 2 }}>
          Viewing existing booking (editing disabled)
        </Typography>
      )}

      <form onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <TextField
            label="Start Time"
            value={form.startTime ? form.startTime.format("DD MMM HH:mm") : ""}
            disabled
            fullWidth
          />

          <Box display="flex" gap={1}>
            <TextField
              label="End Time"
              value={form.endTime ? form.endTime.format("DD MMM HH:mm") : ""}
              disabled
              fullWidth
            />
            <IconButton onClick={incrementEndTime} disabled={isViewMode}>
              <AddIcon />
            </IconButton>
          </Box>

          <TextField
            label="Convenor Name"
            value={form.convenor}
            onChange={(e) => setForm({ ...form, convenor: e.target.value })}
            fullWidth
            required
            disabled={isViewMode}
          />

          <TextField
            label="Purpose"
            value={form.purpose}
            onChange={(e) => setForm({ ...form, purpose: e.target.value })}
            fullWidth
            required
            disabled={isViewMode}
          />

          <TextField
            label="Attendees (Optional)"
            multiline
            minRows={2}
            value={form.attendees}
            onChange={(e) => setForm({ ...form, attendees: e.target.value })}
            fullWidth
            disabled={isViewMode}
          />

          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              color="success"
              onClick={handleSubmit}
              fullWidth
              disabled={isViewMode}
            >
              Submit
            </Button>

            <Button
              variant="outlined"
              onClick={() => {
                resetForm();
                onReset();
              }}
              fullWidth
            >
              Reset
            </Button>
          </Stack>
        </Stack>
      </form>
    </Box>
  );
}
